import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Field } from "@/pages/Companies";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import WebexPanel from "@/components/WebexPanel";
import BankDetailsCard from "@/components/BankDetailsCard";
import ResendSettingsCard from "@/components/ResendSettingsCard";
import DailyDigestCard from "@/components/DailyDigestCard";
import PushNotificationsCard from "@/components/PushNotificationsCard";
import {
  Calendar, FileSpreadsheet, Phone, Apple, ChevronRight, Sparkles, Loader2, CheckCircle2, Wand2
} from "lucide-react";

const PROVIDERS = {
  google_calendar: { name: "Google Calendar", icon: Calendar, accent: "bg-sky-100 text-sky-700", fields: ["client_id", "client_secret"], description: "Two-way sync for tasks, meetings & site appointments." },
  xero: { name: "Xero", icon: FileSpreadsheet, accent: "bg-emerald-100 text-emerald-700", fields: [], badge: "LIVE", description: "Real OAuth — create deposit & final-balance invoices in Xero straight from jobs, and sync paid amounts back into the CRM." },
  webex: { name: "Webex", icon: Phone, accent: "bg-violet-100 text-violet-700", fields: [], description: "Real OAuth — team alerts to a Webex space, call history, meeting transcripts + AI call capture.", badge: "LIVE" },
  apple: { name: "Apple (iCloud / iOS)", icon: Apple, accent: "bg-slate-900 text-white", fields: ["client_id", "client_secret"], description: "iCloud calendar sync + native iOS push notifications." },
};

const FIELD_LABEL = {
  client_id: "Client ID",
  client_secret: "Client Secret",
  tenant_id: "Tenant ID",
  api_key: "API Key / Token",
  webhook_url: "Webhook URL",
};

export default function Integrations() {
  const qc = useQueryClient();
  const [connecting, setConnecting] = useState(null);
  const [form, setForm] = useState({});
  const [webexOpen, setWebexOpen] = useState(false);
  const [webexInput, setWebexInput] = useState({ caller_name: "John Smith", caller_phone: "0411 222 333", caller_email: "john@example.com.au", transcript: "Hi, my name is John Smith. I'm calling about a quote for around 80 metres of pool fencing at 12 Beach Road, Avoca. We've got a backyard pool and need certified fencing — preferably black aluminium tubular. Can someone come out next Tuesday to measure up?" });
  const [webexResult, setWebexResult] = useState(null);
  const [webexLoading, setWebexLoading] = useState(false);
  const [webexPanelOpen, setWebexPanelOpen] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("webex") === "connected") {
      toast.success("Webex connected — pick a team alert space in Manage");
      qc.invalidateQueries({ queryKey: ["integrations"] });
      setWebexPanelOpen(true);
    } else if (p.get("webex") === "error") {
      toast.error("Webex connection failed — please try again");
    }
    if (p.get("xero") === "connected") {
      toast.success("Xero connected — you can now invoice jobs directly");
      qc.invalidateQueries({ queryKey: ["xero-status"] });
    } else if (p.get("xero") === "error") {
      toast.error("Xero connection failed — please try again");
    }
    if (p.has("webex") || p.has("xero")) window.history.replaceState({}, "", window.location.pathname);
  }, [qc]);

  const startWebexOAuth = async () => {
    try {
      const { data } = await api.get("/webex/auth-url");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Webex sign-in");
    }
  };

  const { data: xeroStatus } = useQuery({ queryKey: ["xero-status"], queryFn: async () => (await api.get("/xero/status")).data });
  const startXeroOAuth = async () => {
    try {
      const { data } = await api.get("/xero/auth-url");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Xero sign-in");
    }
  };
  const xeroSync = useMutation({
    mutationFn: async () => (await api.post("/xero/sync")).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["xero-status"] }); toast.success(`Xero sync complete — ${d.updated} invoice${d.updated === 1 ? "" : "s"} updated`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Sync failed"),
  });
  const xeroImport = useMutation({
    mutationFn: async () => (await api.post("/xero/import-contacts")).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success(`Xero import complete — ${d.imported} new client${d.imported === 1 ? "" : "s"} added, ${d.skipped} already existed (${d.total_in_xero} in Xero)`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Xero import failed"),
  });

  const xeroDisconnect = useMutation({
    mutationFn: async () => (await api.post("/xero/disconnect")).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["xero-status"] }); toast.success("Xero disconnected"); },
  });

  const { data: integrations = [] } = useQuery({ queryKey: ["integrations"], queryFn: async () => (await api.get("/integrations")).data });

  const connect = useMutation({
    mutationFn: async ({ provider, payload }) => (await api.post(`/integrations/${provider}/connect`, payload)).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["integrations"] }); setConnecting(null); setForm({}); toast.success(d.message || "Connected"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const disconnect = useMutation({
    mutationFn: async (provider) => (await api.post(`/integrations/${provider}/disconnect`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["integrations"] }); toast.success("Disconnected"); },
  });

  const runWebexDemo = async () => {
    setWebexLoading(true);
    setWebexResult(null);
    try {
      const { data } = await api.post("/integrations/webex/capture-call", webexInput);
      setWebexResult(data);
      toast.success(`Contact ${data.contact_action} and task created`);
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["activities"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Capture failed");
    } finally { setWebexLoading(false); }
  };

  return (
    <div>
      <PageHeader
        title="Integrations"
        subtitle="Connect calendars, accounting, telephony & mobile. Webex uses real OAuth — the rest are mock-wired until keys are added."
        breadcrumb={["System", "Integrations"]}
      />
      <div className="px-6 lg:px-10 py-6 space-y-6">
        <Card className="rounded-sm border-cyan-200 bg-cyan-50/40 p-5 flex items-start gap-3">
          <Sparkles size={20} className="text-cyan-600 shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700">
            <div className="font-display font-semibold text-slate-900">AI is live</div>
            <p className="mt-1 text-slate-600">Quote scope summaries, material recommendations and Webex call-capture all use <b>Claude Sonnet 4.5</b> via the Emergent Universal Key. <span className="text-cyan-700 font-medium">Sintra.ai has no public API</span> — Claude is used directly.</p>
          </div>
        </Card>

        <ResendSettingsCard />

        <PushNotificationsCard />

        <DailyDigestCard />

        <BankDetailsCard />

        <div className="grid lg:grid-cols-2 gap-4">
          {Object.entries(PROVIDERS).map(([key, it]) => {
            const Icon = it.icon;
            const state = integrations.find((i) => i.provider === key) || {};
            const connected = key === "xero" ? !!xeroStatus?.connected : (state.connected && !(key === "webex" && state.mocked));
            return (
              <Card key={key} className="rounded-sm border-slate-200 industrial-shadow overflow-hidden" data-testid={`integration-${key}`}>
                <div className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-12 h-12 flex items-center justify-center ${it.accent}`}>
                      <Icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <div className="font-display text-lg font-semibold text-slate-900">{it.name}</div>
                        {it.badge && <Badge variant="outline" className="rounded-sm text-[9px] border-violet-300 bg-violet-50 text-violet-700">{it.badge}</Badge>}
                        {state.mocked && key !== "webex" && key !== "xero" && <Badge variant="outline" className="rounded-sm text-[9px] border-amber-300 bg-amber-50 text-amber-700">MOCK</Badge>}
                        {key === "xero" && xeroStatus?.needs_reconnect && <Badge variant="outline" className="rounded-sm text-[9px] border-amber-400 bg-amber-50 text-amber-800" data-testid="xero-reconnect-badge">RECONNECT</Badge>}
                      </div>
                      <p className="text-sm text-slate-600">{it.description}</p>
                    </div>
                  </div>
                  {key === "xero" && xeroStatus?.needs_reconnect && (
                    <div className="border border-amber-200 bg-amber-50 text-amber-900 text-xs rounded-sm p-3 mt-2" data-testid="xero-reconnect-banner">
                      <b>Reconnect required for Push-to-Xero-Quote.</b> Your existing invoicing still works, but the new Quote push endpoint needs the <code className="font-mono text-[11px] bg-white px-1 rounded">accounting.transactions</code> scope. Click <b>Disconnect</b> then <b>Connect with Xero</b> again to grant it — takes 15 seconds.
                    </div>
                  )}
                </div>
                <div className="border-t border-slate-100 px-5 py-3 bg-slate-50 flex items-center justify-between">
                  <Badge variant="outline" className={`rounded-sm ${connected ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-300 bg-white text-slate-600"}`}>
                    {connected ? <><CheckCircle2 size={11} className="inline mr-1" /> Connected{key === "xero" && xeroStatus?.org_name ? ` — ${xeroStatus.org_name}` : ""}</> : "Not connected"}
                  </Badge>
                  <div className="flex gap-2">
                    {key === "xero" && connected && (
                      <>
                        <Button data-testid="xero-import-btn" size="sm" variant="outline" onClick={() => xeroImport.mutate()} disabled={xeroImport.isPending} className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                          {xeroImport.isPending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : null} Import customers
                        </Button>
                        <Button data-testid="xero-sync-btn" size="sm" onClick={() => xeroSync.mutate()} disabled={xeroSync.isPending} className="rounded-sm bg-emerald-600 hover:bg-emerald-700">
                          {xeroSync.isPending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : null} Sync payments
                        </Button>
                      </>
                    )}
                    {key === "webex" && connected && (
                      <>
                        <Button data-testid="webex-manage-btn" size="sm" onClick={() => setWebexPanelOpen(true)} className="rounded-sm bg-violet-600 hover:bg-violet-700">
                          Manage
                        </Button>
                        <Button data-testid="webex-test-btn" size="sm" variant="outline" onClick={() => setWebexOpen(true)} className="rounded-sm">
                          <Wand2 size={13} className="mr-1.5" /> AI capture
                        </Button>
                      </>
                    )}
                    {connected ? (
                      <Button data-testid={`disconnect-${key}-btn`} size="sm" variant="outline" onClick={() => (key === "xero" ? xeroDisconnect.mutate() : disconnect.mutate(key))} className="rounded-sm">Disconnect</Button>
                    ) : key === "webex" ? (
                      <Button data-testid="connect-webex-btn" size="sm" onClick={startWebexOAuth} className="rounded-sm bg-violet-600 hover:bg-violet-700">
                        Connect with Webex <ChevronRight size={14} className="ml-1" />
                      </Button>
                    ) : key === "xero" ? (
                      <Button data-testid="connect-xero-btn" size="sm" onClick={startXeroOAuth} className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
                        title={xeroStatus && !xeroStatus.has_client_secret ? "Waiting on Xero client secret" : undefined}>
                        Connect with Xero <ChevronRight size={14} className="ml-1" />
                      </Button>
                    ) : (
                      <Button data-testid={`connect-${key}-btn`} size="sm" onClick={() => { setConnecting(key); setForm({}); }} className="rounded-sm bg-slate-900 hover:bg-slate-800">
                        Connect <ChevronRight size={14} className="ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Connect dialog */}
      <Dialog open={!!connecting} onOpenChange={(o) => !o && setConnecting(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="connect-dialog">
          {connecting && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">Connect {PROVIDERS[connecting].name}</DialogTitle>
              </DialogHeader>
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 p-3 rounded-sm">
                <b>Mock OAuth:</b> in production this would launch the official OAuth2 flow. For now, paste any test values — the connection will be recorded and downstream actions (sync activity, export quote, capture call) will all work against your data.
              </div>
              <div className="space-y-3 mt-2">
                {PROVIDERS[connecting].fields.map((f) => (
                  <Field key={f} label={FIELD_LABEL[f]}>
                    <Input data-testid={`field-${f}`} value={form[f] || ""} onChange={(e) => setForm({ ...form, [f]: e.target.value })} placeholder={`Enter ${FIELD_LABEL[f]}`} />
                  </Field>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConnecting(null)} className="rounded-sm">Cancel</Button>
                <Button data-testid="confirm-connect-btn" onClick={() => connect.mutate({ provider: connecting, payload: form })} disabled={connect.isPending} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">
                  {connect.isPending ? <Loader2 size={14} className="animate-spin" /> : "Connect"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <WebexPanel open={webexPanelOpen} onOpenChange={setWebexPanelOpen} />

      {/* Webex AI capture demo */}
      <Dialog open={webexOpen} onOpenChange={setWebexOpen}>
        <DialogContent className="rounded-sm sm:max-w-2xl" data-testid="webex-demo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Webex AI Call Capture — Demo</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-600">Paste a call transcript (as Webex AI would deliver). Claude extracts the caller's details, auto-creates a contact in the database, and schedules a follow-up task.</p>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-3 gap-2">
              <Field label="Caller name"><Input data-testid="webex-name" value={webexInput.caller_name} onChange={(e) => setWebexInput({ ...webexInput, caller_name: e.target.value })} /></Field>
              <Field label="Phone"><Input value={webexInput.caller_phone} onChange={(e) => setWebexInput({ ...webexInput, caller_phone: e.target.value })} /></Field>
              <Field label="Email"><Input value={webexInput.caller_email} onChange={(e) => setWebexInput({ ...webexInput, caller_email: e.target.value })} /></Field>
            </div>
            <Field label="Call transcript">
              <Textarea data-testid="webex-transcript" rows={5} value={webexInput.transcript} onChange={(e) => setWebexInput({ ...webexInput, transcript: e.target.value })} />
            </Field>
          </div>
          {webexResult && (
            <div className="border border-emerald-200 bg-emerald-50 p-3 mt-3 rounded-sm text-xs space-y-1">
              <div className="font-display font-semibold text-emerald-900 flex items-center gap-1"><CheckCircle2 size={14} /> Capture complete</div>
              <div>Contact {webexResult.contact_action}: <span className="font-mono">{webexResult.contact_id}</span></div>
              <div>Task created: <span className="font-mono">{webexResult.task_id}</span></div>
              <div>Extracted task: <b>{webexResult.extracted?.follow_up_task}</b></div>
              {webexResult.extracted?.address && <div>Site: {webexResult.extracted.address}</div>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setWebexOpen(false)} className="rounded-sm">Close</Button>
            <Button data-testid="webex-run-btn" onClick={runWebexDemo} disabled={webexLoading} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">
              {webexLoading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Wand2 size={14} className="mr-1.5" />}
              Run AI capture
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
